import React, { Suspense } from 'react';
import { createPortal } from 'react-dom';
import ReactPlayer from 'react-player';
import {
  MediaController,
  MediaControlBar,
  MediaTimeRange,
  MediaTimeDisplay,
  MediaVolumeRange,
  MediaPlaybackRateButton,
  MediaPlayButton,
  MediaSeekBackwardButton,
  MediaSeekForwardButton,
  MediaMuteButton,
  MediaFullscreenButton,
} from 'media-chrome/react';
import { Play, X } from 'lucide-react';
import { env } from '@/utils/constants/env';
import { cn } from '@/utils/lib/utils';

interface VideoPlayerProps {
  url: string;
  title?: string;
  className?: string;
  defaultOpen?: boolean;
  tipsHtml?: string;
}

const VideoPlayer: React.FC<VideoPlayerProps> = ({ url, title, className, defaultOpen = false, tipsHtml }) => {
  const [isModalOpen, setIsModalOpen] = React.useState(defaultOpen);
  const normalizedUrl = url?.trim() ?? '';
  const hasValidUrl = normalizedUrl.length > 0;

  // Handle relative URLs
  const BASE_URL = env.minioBaseUrl || '';
  const finalUrl = normalizedUrl.startsWith('http')
    ? normalizedUrl
    : BASE_URL + (normalizedUrl.startsWith('/') ? '' : '/') + normalizedUrl;

  // Handle potential default export handle for library variations.
  // ReactPlayer's prop typings vary across versions, so it is treated as a
  // permissive component to allow library-specific props (url/src/playerVars).
  const Player = ((ReactPlayer as unknown as { default?: unknown }).default || ReactPlayer) as React.ComponentType<
    Record<string, unknown>
  >;

  React.useEffect(() => {
    if (!isModalOpen || !hasValidUrl) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isModalOpen, hasValidUrl]);

  if (!hasValidUrl) return null;

  return (
    <>
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => setIsModalOpen(true)}
          data-testid="open-video-button"
          className={cn(
            'inline-flex shrink-0 max-w-full items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-sm font-medium text-blue-700 transition-colors hover:bg-blue-100 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700',
            className,
          )}
        >
          <Play className="h-4 w-4 fill-current" />
          <span>Tonton Video Panduan</span>
        </button>
      </div>

      {isModalOpen &&
        typeof document !== 'undefined' &&
        createPortal(
          <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 md:p-6">
            <div
              aria-hidden="true"
              className="absolute inset-0 bg-slate-900/30 backdrop-blur-sm cursor-pointer"
              onClick={() => setIsModalOpen(false)}
            />
            <div className="relative z-10 w-full max-w-5xl max-h-[calc(100vh-2rem)] flex flex-col overflow-hidden rounded-xl bg-black shadow-2xl">
              <div className="shrink-0 flex items-center justify-between bg-slate-900/85 px-4 py-3 text-white">
                <h3 className="text-sm font-medium">{title || 'Video Panduan'}</h3>
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  data-testid="close-video-button"
                  aria-label="Tutup video panduan"
                  className="rounded-md border border-white/20 p-1.5 text-white transition-colors hover:bg-white/15"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto w-full flex flex-col">
                <Suspense
                  fallback={
                    <div className="aspect-video flex items-center justify-center text-white font-medium">
                      Memuat Video...
                    </div>
                  }
                >
                  <MediaController
                    style={{
                      width: '100%',
                      aspectRatio: '16/9',
                    }}
                  >
                    <Player
                      slot="media"
                      url={finalUrl}
                      src={finalUrl}
                      width="100%"
                      height="100%"
                      controls={false}
                      playing={true}
                      playsInline={true}
                      config={{
                        youtube: {
                          playerVars: {
                            origin: window.location.origin,
                            modestbranding: 1,
                            rel: 0,
                          },
                        },
                      }}
                    />
                    <MediaControlBar>
                      <MediaPlayButton />
                      <MediaSeekBackwardButton seekOffset={10} />
                      <MediaSeekForwardButton seekOffset={10} />
                      <MediaTimeRange />
                      <MediaTimeDisplay showDuration />
                      <MediaMuteButton />
                      <MediaVolumeRange />
                      <MediaPlaybackRateButton />
                      <MediaFullscreenButton />
                    </MediaControlBar>
                  </MediaController>
                </Suspense>
                {tipsHtml && (
                  <div
                    className="shrink-0 max-h-48 overflow-y-auto bg-slate-900/85 px-4 py-3 text-sm text-white/90 border-t border-slate-700"
                    dangerouslySetInnerHTML={{ __html: tipsHtml }}
                  />
                )}
              </div>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
};

export default VideoPlayer;
